import { Graphics } from "pixi.js";
import { nailPullFrame, StageFit } from "./stageMath";
import {
  drawFastenerEdgeMark,
  drawFastenerHead,
} from "../../../views/material-sprites/fastenerHead";
import { Persistence } from "../../../config/constants";
import { BaseEntity } from "../../../core/entity/BaseEntity";
import { Entity } from "../../../core/entity/Entity";
import { GameSprite } from "../../../core/entity/GameSprite";
import { on } from "../../../core/entity/handler";
import {
  armedFasteners,
  FASTENER_HIT_RADIUS_IN,
  fastenerAt,
  fastenerHeadingOnBench,
  fastenerOnBench,
  fastenedPieceIds,
  fastenedPiecesOf,
  groupSeating,
  slotOnBench,
  snapPlacementFor,
} from "../../../game/bench-work/assembly";
import {
  slotFaceOutline,
  slotFootprintIn,
} from "../../../game/bench-work/plan-geometry";
import { BenchPlacement } from "../../../game/bench-work/bench-layout";
import {
  BlueprintFastener,
  BlueprintSlot,
  fastenerToolId,
  ProductBlueprint,
  productBlueprintFor,
} from "../../../game/bench-work/blueprint";
import {
  CLAW_LEVER,
  DRIVER_TUNING,
  HAMMER_AT_REST,
  HAMMER_TUNING,
  hammerCursor,
  HammerCursor,
  hammerDown,
  hammerHold,
  hammerMove,
  HammerOutcome,
  HammerState,
  HammerTuning,
  hammerUp,
} from "../../../game/bench-work/hammer-gesture";
import { selectedBenchPlan } from "../../../game/bench-work/tool-work";
import { ConsumableId } from "../../../game/Consumable";
import { MaterialInstance } from "../../../game/Materials";
import { ToolId } from "../../../game/Tool";
import {
  driveFastener,
  pryFastener,
} from "../../../sim/commands/assembly-commands";
import { gatherBenchPieces } from "../../../sim/commands/bench-commands";
import {
  finishAttendedWork,
  operateMachine,
} from "../../../sim/commands/machine-commands";
import { MachineEntity } from "../../../sim/entities/MachineEntity";
import { Consumables } from "../../../sim/singletons/Consumables";
import { playSound } from "../../../utils/sfx";
import { RewardFlights, supplyChip } from "../../RewardFlights";
import { ShellStore } from "../../ShellStore";
import { BenchArrangeView } from "./BenchArrangeView";
import { BenchDive } from "./BenchDive";
import { trayUnderPointer } from "./BenchTrayView";
import {
  benchStage,
  openBenchGroup,
  stagePointer,
  stageSettled,
} from "./benchStage";

/**
 * Blueprint assembly on the bench itself (migration phase 7, the last
 * of the four modes): the drawing pulled off the pile puts ghost
 * outlines on the bench top, the player lays each part on its outline —
 * a drag released near one settles onto it — and the crossings where two
 * seated parts meet arm for the fastener the drawing names: down through
 * a lap, or level through a face into a board end, where the target
 * sits on the part's outline with a tail showing the way in. The hammer
 * is one press read three ways (`bench-work/hammer-gesture.ts`): a quick
 * still release swings — three timed blows set a nail, the head sinking
 * with each, and the last blow drives the fastener for real (one leaves
 * the supplies, the head is recorded on the bench, `driveFastener`);
 * holding turns the hammer over into the claw, and dragging along the
 * lever pulls a nail back out (`pryFastener`), the fastener flying home
 * to the supplies tally. The drill sets a screw in one press and backs
 * one out the same way. The last fastener driven commits the whole build.
 *
 * Nothing here outlives the press but the blows on a nail not yet set
 * (mid-action progress, decision 3): seating is read back out of where
 * the pieces lie (`groupSeating` over the run's layout) and the driven
 * fasteners out of the bench's own record (`MachineState.assembly`), so
 * standing up and coming back — or refreshing — finds every part seated
 * and every set nail in exactly as it was left (decision 4 in
 * docs/bench-work.md). With none of the drawing's fastener on hand the
 * crossings stop offering themselves, and the key hints say where more
 * come from.
 */

const GHOST = 0xf5efe3;
const GHOST_CANDIDATE = 0xd97c26;

/** How long a blow's flash, a miss's puff, or a pop lingers over a head. */
const EFFECT_SECONDS = 0.24;

/** A drawn nail's length at this zoom — a real nail's, capped so it stays
 * a nail and not a spike at the closest lean-in (the pallet pry's
 * numbers), and how far past the hole it ends up when pulled. */
const NAIL_LENGTH_IN = 2.5;
const NAIL_LENGTH_MAX_PX = 44;
const NAIL_CLEARANCE_PX = 8;
/** How much of the claw's lever line to draw past the head, in px. */
const LEVER_DRAW_PX = 28;

const CLAW = 0x8a8378;
const NAIL_STEEL = 0xc9c3b8;
const NAIL_HEAD = 0x8f8a80;
const DUST = 0xd8cdb8;

export class BenchAssemblyView extends BaseEntity implements Entity {
  persistenceLevel: number = Persistence.Permanent;
  pausable = false;

  private layer: Graphics & GameSprite;
  /** The hammer's press, read by the pure gesture module. */
  private hammer: HammerState = HAMMER_AT_REST;
  /** The heads under the press: the armed crossing a swing would drive
   * and the driven head a pull would take — the nearer of the two, or
   * both where they share a spot — and where they sit on the stage. */
  private pressed: {
    armed: BlueprintFastener | null;
    driven: BlueprintFastener | null;
    x: number;
    y: number;
  } | null = null;
  /** Full blows landed on each armed crossing so far: a nail standing
   * proud until its last blow sets it. Ephemeral, like a half-sanded
   * pass (decision 3). */
  private blows = new Map<BlueprintFastener, number>();
  /** The blow, glance, miss, pop, or slide playing out over a head. */
  private effect: {
    kind: "blow" | "glance" | "miss" | "pop" | "slide";
    x: number;
    y: number;
    secondsLeft: number;
  } | null = null;
  /** Every swing so far, and when the last one came down — the cursor
   * restarts its swing on each. */
  private swings = 0;
  private swungAt = -Infinity;
  /** The armed crossing under the hand, for the ring that warms. */
  private hovered: BlueprintFastener | null = null;
  /** The outline a dragged part would settle on, updated each tick. */
  private snapSlot: string | null = null;

  constructor() {
    super();
    // Placed in the dive's own frame ("parent" — this view is the dive
    // view's child), so the lean-in carries every surface on the stage
    // as one picture.
    this.layer = new Graphics() as Graphics & GameSprite;
    this.layer.layerName = "parent";
    this.sprite = this.layer;
  }

  /** How the build stands: parts seated and fasteners driven, of how
   * many the drawing asks for. Null when no drawing is out. */
  assemblyProgress(): {
    seated: number;
    slots: number;
    driven: number;
    fasteners: number;
    /** What the drawing fastens with, how many the shop has on hand,
     * and whether the tool that drives it is in hand. */
    fastenerId: ConsumableId;
    onHand: number;
    driver: ToolId | null;
    driverHeld: boolean;
    /** An empty outline wanting a piece stood up, and how. */
    tipUp: { role: string; onEnd: boolean } | null;
  } | null {
    const build = this.build();
    if (!build) return null;
    const tipUp = build.blueprint.slots.find(
      (slot) => !build.seated.has(slot.id) && (slot.onEdge || slot.onEnd),
    );
    return {
      seated: build.seated.size,
      slots: build.blueprint.slots.length,
      driven: build.driven.length,
      fasteners: build.blueprint.fasteners.length,
      fastenerId: build.blueprint.fastenerConsumable,
      onHand: this.onHand(build.blueprint),
      driver: fastenerToolId(build.blueprint.fastenerConsumable),
      driverHeld: this.driverHeld(build.blueprint),
      tipUp: tipUp ? { role: tipUp.role, onEnd: tipUp.onEnd === true } : null,
    };
  }

  /**
   * The drawing's top-left corner, in the run's frame: where its ghost
   * outlines start, and what a spec measures a part's landing from.
   */
  productCorner(): { xIn: number; yIn: number } | null {
    const build = this.build();
    if (!build) return null;
    return {
      xIn: build.productPlacement.xIn - build.blueprint.widthIn / 2,
      yIn: build.productPlacement.yIn - build.blueprint.heightIn / 2,
    };
  }

  /**
   * The empty outline a dragged part would settle on if it were let go
   * now — the one the view lights up. Null with nothing in hand.
   */
  snapCandidate(): string | null {
    return this.snapSlot;
  }

  /**
   * The empty outline under the pointer, when the hands are free — what
   * the tag beside the cursor names. Null with anything in hand, mid-
   * drag, or over an outline that already has its part.
   */
  hoveredSlot(): BlueprintSlot | null {
    const build = this.build();
    const stage = benchStage(this.game);
    const dive = this.dive();
    if (!build || !stage || !dive || dive.handsFull()) return null;
    if (this.game.entities.tryGetSingleton(BenchArrangeView)?.draggingId()) {
      return null;
    }
    const at = stagePointer(this.game, stage.fit);
    for (const slot of build.blueprint.slots) {
      if (build.seated.has(slot.id)) continue;
      const seat = slotOnBench(build.blueprint, build.productPlacement, slot);
      const { wIn, hIn } = slotFootprintIn(slot);
      const across = (seat.angleDeg ?? 0) % 180 !== 0;
      const halfW = (across ? hIn : wIn) / 2;
      const halfH = (across ? wIn : hIn) / 2;
      if (
        Math.abs(at.xIn - seat.xIn) <= halfW &&
        Math.abs(at.yIn - seat.yIn) <= halfH
      ) {
        return slot;
      }
    }
    return null;
  }

  /** Where the armed crossings sit on screen — the seam a spec drives
   * a real fastener through. */
  fastenerPoints(): ReadonlyArray<{ x: number; y: number }> {
    const build = this.build();
    const stage = benchStage(this.game);
    if (!build || !stage) return [];
    return this.armed(build).map((fastener) => {
      const at = fastenerOnBench(
        build.blueprint,
        build.productPlacement,
        fastener,
      );
      return {
        x: stage.fit.originX + at.xIn * stage.fit.pxPerIn,
        y: stage.fit.originY + at.yIn * stage.fit.pxPerIn,
      };
    });
  }

  /** Where each empty outline sits on screen, by slot id. */
  slotPoints(): ReadonlyArray<{ id: string; x: number; y: number }> {
    const build = this.build();
    const stage = benchStage(this.game);
    if (!build || !stage) return [];
    return build.blueprint.slots.map((slot) => {
      const seat = slotOnBench(build.blueprint, build.productPlacement, slot);
      return {
        id: slot.id,
        x: stage.fit.originX + seat.xIn * stage.fit.pxPerIn,
        y: stage.fit.originY + seat.yIn * stage.fit.pxPerIn,
      };
    });
  }

  private dive(): BenchDive | undefined {
    return this.game.entities.tryGetSingleton(BenchDive);
  }

  /**
   * The build in front of the player: the drawing out on the bench, the
   * frame it sits in, and which piece is on which outline. Null when no
   * drawing is pulled or the bench is already running something.
   */
  private build(): {
    blueprint: ProductBlueprint;
    productPlacement: BenchPlacement;
    seated: ReadonlyMap<string, string>;
    pieces: Array<{ material: MaterialInstance; placement: BenchPlacement }>;
    /** The drawing's fasteners already in, in the order they went in. */
    driven: ReadonlyArray<BlueprintFastener>;
  } | null {
    const dive = this.dive();
    const run = openBenchGroup(this.game);
    if (!dive || dive.openBenchKey === null || !run) return null;
    const opened = run.opened;
    if (opened.operationProgress.status === "inProgress") return null;
    const plan = selectedBenchPlan(opened);
    const interaction = plan?.interaction;
    if (!plan || interaction?.kind !== "assembly") return null;
    const blueprint = productBlueprintFor(interaction.blueprint);
    if (!blueprint) return null;
    const { productPlacement, pieces, seated } = groupSeating(
      run.group,
      opened,
      blueprint,
    );
    // The bench's record of what's driven belongs to this drawing; a
    // record for another plan (a fixture's) drives nothing here
    const record = opened.state.assembly;
    const driven =
      record && record.planId === blueprint.id
        ? record.driven
            .map((index) => blueprint.fasteners[index])
            .filter((fastener) => fastener !== undefined)
        : [];
    return { blueprint, productPlacement, seated, pieces, driven };
  }

  /** Crossings whose both parts are seated and not yet driven. */
  private armed(
    build: NonNullable<ReturnType<BenchAssemblyView["build"]>>,
  ): ReadonlyArray<BlueprintFastener> {
    return armedFasteners(build.blueprint, build.seated).filter(
      (fastener) => !build.driven.includes(fastener),
    );
  }

  /** How many of the drawing's fastener the shop has to drive. */
  private onHand(blueprint: ProductBlueprint): number {
    return (
      this.game.entities.tryGetSingleton(Consumables)?.stock[
        blueprint.fastenerConsumable
      ] ?? 0
    );
  }

  /**
   * What the hand's driver is doing, for the cursor: rest, cocked, or
   * turned to the claw, how far it has come back up since the last
   * swing, how far the pull has come, and the swing count so the swing
   * animation restarts each time. Null while the drawing's tool isn't
   * in hand.
   */
  hammerCursor(): {
    cursor: HammerCursor;
    swings: number;
    swungAt: number;
  } | null {
    const build = this.build();
    if (!build || !this.driverHeld(build.blueprint)) return null;
    return {
      cursor: hammerCursor(
        this.hammer,
        performance.now(),
        this.tuning(build.blueprint),
      ),
      swings: this.swings,
      swungAt: this.swungAt,
    };
  }

  /** The hammer's rhythm for nails, the powered driver's for screws. */
  private tuning(blueprint: ProductBlueprint): HammerTuning {
    return blueprint.fastenerConsumable === "screws"
      ? DRIVER_TUNING
      : HAMMER_TUNING;
  }

  /** Whether the drawing's own driving tool is in hand. */
  private driverHeld(blueprint: ProductBlueprint): boolean {
    const tool = fastenerToolId(blueprint.fastenerConsumable);
    return tool !== null && this.dive()?.heldTool === tool;
  }

  /**
   * Where a dragged piece would settle if it were let go now — the
   * arranging view asks this on release, so a part laid near its outline
   * lands *on* it.
   */
  snapFor(
    material: MaterialInstance,
    placement: BenchPlacement,
  ): BenchPlacement | null {
    const build = this.build();
    if (!build) return null;
    const taken = new Set(
      [...build.seated.entries()]
        .filter(([, materialId]) => materialId !== material.id)
        .map(([slotId]) => slotId),
    );
    return (
      snapPlacementFor(
        build.blueprint,
        build.productPlacement,
        material,
        placement,
        taken,
      )?.placement ?? null
    );
  }

  /**
   * Pieces a driven fastener holds: nailed on, so they don't move. Read
   * off every table in the run's own record, so the lock holds whatever
   * drawing is out and whatever else the bench is doing.
   */
  fastened(): ReadonlySet<string> {
    const run = openBenchGroup(this.game);
    const locked = new Set<string>();
    for (const member of run?.group.members ?? []) {
      for (const id of fastenedPiecesOf(member.machine)) locked.add(id);
    }
    const build = this.build();
    if (build) {
      for (const id of fastenedPieceIds(build.seated, build.driven)) {
        locked.add(id);
      }
    }
    return locked;
  }

  /** Recompute which outline is lit, and tell the chrome when it moves. */
  private updateSnapCandidate(
    build: ReturnType<BenchAssemblyView["build"]>,
  ): void {
    const dragging = this.game.entities
      .tryGetSingleton(BenchArrangeView)
      ?.draggingId();
    const next = build && dragging ? this.snapSlotFor(build, dragging) : null;
    if (next === this.snapSlot) return;
    this.snapSlot = next;
    // The stage marker publishes the lit outline, so a move is a redraw.
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  @on("tick")
  onTick(dt: number) {
    if (this.effect) {
      this.effect.secondsLeft -= dt;
      if (this.effect.secondsLeft <= 0) this.effect = null;
    }
    const build = this.build();
    // Before the guards below: a part is dragged with the hands free, so
    // no driver is held and the fastener half of this tick stands down.
    this.updateSnapCandidate(build);
    if (!build) {
      this.letGo();
      return;
    }
    // A build with no fasteners at all — two planks laid side by side —
    // has nothing to drive: laying the last part on IS the whole build.
    if (
      build.blueprint.fasteners.length === 0 &&
      build.seated.size === build.blueprint.slots.length &&
      build.blueprint.slots.length > 0
    ) {
      this.commit(build);
      return;
    }
    // Blows on a crossing that isn't armed any more (its part moved, or
    // it was set) are spent
    const armed = this.armed(build);
    for (const fastener of [...this.blows.keys()]) {
      if (!armed.includes(fastener)) this.blows.delete(fastener);
    }

    const stage = benchStage(this.game);
    const bench = this.dive()?.openBench();
    if (
      !stage ||
      !bench ||
      !stageSettled(this.game) ||
      !this.driverHeld(build.blueprint)
    ) {
      this.letGo();
      return;
    }
    const [px, py] = this.game.io.mousePosition;
    if (!this.hammer.press || !this.pressed) {
      const at = stagePointer(this.game, stage.fit);
      this.hovered = this.headsAt(build, at, stage.fit)?.armed ?? null;
      return;
    }
    // The button is down: time passes on the hold, the hand may move
    const now = performance.now();
    const tuning = this.tuning(build.blueprint);
    const overTarget =
      Math.hypot(px - this.pressed.x, py - this.pressed.y) <=
      FASTENER_HIT_RADIUS_IN * stage.fit.pxPerIn;
    const held = hammerHold(this.hammer, now, px, py, overTarget, tuning);
    this.hammer = held.state;
    if (held.outcome) this.resolve(held.outcome, build, bench, stage.fit);
    const moved = hammerMove(
      this.hammer,
      px,
      py,
      this.lever(build, this.pulling()),
      tuning,
    );
    this.hammer = moved.state;
    if (moved.outcome) this.resolve(moved.outcome, build, bench, stage.fit);
  }

  /** The press ends without resolving: the drawing went away, the tool
   * was put down, or the stage moved. Blows on unset nails stay. */
  private letGo(): void {
    this.hammer = { ...HAMMER_AT_REST, swungMs: this.hammer.swungMs };
    this.pressed = null;
    this.hovered = null;
  }

  /** The head a pull under the current press would take: a nail with
   * blows in it first (it's on top), else the set one. */
  private pulling(): BlueprintFastener | null {
    const pressed = this.pressed;
    if (!pressed) return null;
    if (pressed.armed && (this.blows.get(pressed.armed) ?? 0) > 0) {
      return pressed.armed;
    }
    return pressed.driven;
  }

  /**
   * The claw's lever for a head, on screen: a nail driven level comes
   * back out the way it went in, one driven down levers up and to the
   * right. Unit length.
   */
  private lever(
    build: NonNullable<ReturnType<BenchAssemblyView["build"]>>,
    fastener: BlueprintFastener | null,
  ): { dx: number; dy: number } {
    const d = fastener
      ? fastenerHeadingOnBench(
          build.blueprint,
          build.productPlacement,
          fastener,
        )
      : null;
    if (!d) return CLAW_LEVER;
    const length = Math.hypot(d.dx, d.dy) || 1;
    return { dx: -d.dx / length, dy: -d.dy / length };
  }

  /**
   * The heads under the hand: the nearest armed crossing and the nearest
   * driven head within reach — both when they share a spot (level nails
   * into one board end stack in plan), else only the nearer. The gesture
   * decides which one the press means: a swing drives, a pull takes.
   */
  private headsAt(
    build: NonNullable<ReturnType<BenchAssemblyView["build"]>>,
    at: { xIn: number; yIn: number },
    fit: StageFit,
  ): NonNullable<BenchAssemblyView["pressed"]> | null {
    const distance = (fastener: BlueprintFastener) => {
      const head = fastenerOnBench(
        build.blueprint,
        build.productPlacement,
        fastener,
      );
      return Math.hypot(head.xIn - at.xIn, head.yIn - at.yIn);
    };
    let armed = fastenerAt(
      build.blueprint,
      build.productPlacement,
      this.armed(build),
      at.xIn,
      at.yIn,
    );
    // The last one in comes out first where heads stack
    let driven = fastenerAt(
      build.blueprint,
      build.productPlacement,
      [...build.driven].reverse(),
      at.xIn,
      at.yIn,
    );
    if (!armed && !driven) return null;
    if (armed && driven && Math.abs(distance(armed) - distance(driven)) > 0.5) {
      if (distance(armed) < distance(driven)) driven = null;
      else armed = null;
    }
    const head = fastenerOnBench(
      build.blueprint,
      build.productPlacement,
      (armed ?? driven)!,
    );
    return {
      armed,
      driven,
      x: fit.originX + head.xIn * fit.pxPerIn,
      y: fit.originY + head.yIn * fit.pxPerIn,
    };
  }

  /**
   * The press begins over a head: the hammer cocks. What it means is
   * decided at the release, or when the hold turns the claw
   * (`hammer-gesture.ts`).
   */
  @on("mouseDown")
  onMouseDown() {
    const build = this.build();
    const stage = benchStage(this.game);
    const bench = this.dive()?.openBench();
    if (!build || !stage || !bench || !stageSettled(this.game)) return;
    if (!this.driverHeld(build.blueprint)) return;
    // Over a tray the press is the tray's (setting the driver down)
    if (trayUnderPointer(this.game) !== null) return;
    const at = stagePointer(this.game, stage.fit);
    const heads = this.headsAt(build, at, stage.fit);
    if (!heads) return;
    // Using the driver hangs it: one taken up out of a tray goes on the
    // rail with the first press on a head.
    if (!this.dive()?.settleHeldTool()) return;
    const [px, py] = this.game.io.mousePosition;
    this.pressed = heads;
    this.hammer = hammerDown(this.hammer, "head", px, py, performance.now());
    this.hovered = null;
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  @on("mouseUp")
  onMouseUp() {
    if (!this.hammer.press) return;
    const build = this.build();
    const stage = benchStage(this.game);
    const bench = this.dive()?.openBench();
    const tuning = this.tuning(
      build?.blueprint ?? ({ fastenerConsumable: "nails" } as ProductBlueprint),
    );
    const { state, outcome } = hammerUp(this.hammer, performance.now(), tuning);
    this.hammer = state;
    if (outcome && build && stage && bench) {
      this.resolve(outcome, build, bench, stage.fit);
    }
    this.pressed = null;
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  /**
   * One outcome of the press, acted on. A full blow sinks the nail a
   * step and the last one drives the fastener for real — the command
   * spends one out of the supplies and records the head, and the last
   * fastener commits the whole build: because the ghost frame is the
   * finished piece's own seat, it lands exactly where the parts were
   * lying. A completed pull takes a set fastener back out (or knocks the
   * blows out of one not yet set) and flies it home to the tally.
   */
  private resolve(
    outcome: HammerOutcome,
    build: NonNullable<ReturnType<BenchAssemblyView["build"]>>,
    bench: MachineEntity,
    fit: StageFit,
  ): void {
    const pressed = this.pressed;
    if (!pressed) return;
    const screws = build.blueprint.fastenerConsumable === "screws";
    const [px, py] = this.game.io.mousePosition;
    const swung = () => {
      this.swings += 1;
      this.swungAt = performance.now();
    };
    switch (outcome.kind) {
      case "blow": {
        swung();
        const target = pressed.armed;
        // A blow on a set head, or with nothing to drive, just knocks
        if (!target || this.onHand(build.blueprint) === 0) {
          playSound("hammer-miss", 0.4);
          this.effect = {
            kind: "glance",
            x: pressed.x,
            y: pressed.y,
            secondsLeft: EFFECT_SECONDS,
          };
          return;
        }
        const blows = (this.blows.get(target) ?? 0) + 1;
        const toSet = this.tuning(build.blueprint).blowsToSet;
        playSound(
          screws ? "drill-driver" : "hammer-blow",
          0.5,
          "sfx",
          screws ? 1 : 1 + (blows - 1) * 0.08,
        );
        this.effect = {
          kind: "blow",
          x: pressed.x,
          y: pressed.y,
          secondsLeft: EFFECT_SECONDS,
        };
        if (blows < toSet) {
          this.blows.set(target, blows);
          return;
        }
        this.blows.delete(target);
        const index = build.blueprint.fasteners.indexOf(target);
        driveFastener(this.game, bench, index);
        return;
      }
      case "glance":
        swung();
        playSound("hammer-glance", 0.35);
        this.effect = {
          kind: "glance",
          x: pressed.x,
          y: pressed.y,
          secondsLeft: EFFECT_SECONDS,
        };
        return;
      case "miss":
        swung();
        playSound("hammer-miss", 0.45);
        this.effect = {
          kind: "miss",
          x: px,
          y: py,
          secondsLeft: EFFECT_SECONDS * 1.5,
        };
        return;
      case "clawSet":
        playSound("claw-set", 0.4);
        return;
      case "pulled": {
        const target = this.pulling();
        if (!target) return;
        this.effect = {
          kind: "pop",
          x: pressed.x,
          y: pressed.y,
          secondsLeft: EFFECT_SECONDS,
        };
        if (build.driven.includes(target)) {
          const index = build.blueprint.fasteners.indexOf(target);
          if (!pryFastener(this.game, bench, index)) return;
          this.launchFastenerFlight(build, target, fit);
        } else {
          // Not set yet: the blows come out with it, nothing to return
          this.blows.delete(target);
          playSound("nail-pry", 0.5);
        }
        return;
      }
      case "slidBack":
        this.effect = {
          kind: "slide",
          x: pressed.x,
          y: pressed.y,
          secondsLeft: EFFECT_SECONDS,
        };
        return;
      case "lowered":
        return;
    }
  }

  /** A pulled fastener flies from its head to the supplies tally, the
   * way a pried pallet nail does; the tally catches up when it lands. */
  private launchFastenerFlight(
    build: NonNullable<ReturnType<BenchAssemblyView["build"]>>,
    fastener: BlueprintFastener,
    fit: StageFit,
  ): void {
    const flights = this.game.entities.tryGetSingleton(RewardFlights);
    const canvas = this.game.renderer?.canvas;
    if (!flights || !canvas) return;
    const at = fastenerOnBench(
      build.blueprint,
      build.productPlacement,
      fastener,
    );
    const rect = canvas.getBoundingClientRect();
    const id = build.blueprint.fastenerConsumable;
    const flightId = `pull-${fastener.xIn}-${fastener.yIn}-${performance.now()}`;
    const lever = this.lever(build, fastener);
    const clear = nailLengthPx(fit) + NAIL_CLEARANCE_PX;
    flights.launch({
      id: flightId,
      originX:
        rect.left + fit.originX + at.xIn * fit.pxPerIn + lever.dx * clear,
      originY: rect.top + fit.originY + at.yIn * fit.pxPerIn + lever.dy * clear,
      chips: [supplyChip(flightId, id, "nail", 0)],
      supply: { id, amount: 1 },
    });
  }

  /**
   * Start and finish back to back for a build with nothing to drive: the
   * laying-on was the build. (A fastened build commits through its last
   * `driveFastener`.)
   */
  private commit(
    build: NonNullable<ReturnType<BenchAssemblyView["build"]>>,
  ): void {
    const bench = this.dive()?.openBench();
    if (!bench) return;
    // A build's parts may have been laid on from either table of a run;
    // the operation claims from one, so gather them onto it first.
    gatherBenchPieces(
      this.game,
      bench,
      build.pieces.map((piece) => piece.material.id),
    );
    if (operateMachine(this.game, bench)) {
      finishAttendedWork(this.game, bench);
    }
    this.effect = null;
    this.letGo();
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }

  @on("render")
  onRender() {
    const g = this.layer;
    g.clear();
    const build = this.build();
    const stage = benchStage(this.game);
    if (!build || !stage) return;
    const fit = stage.fit;

    // The empty outlines, and the one a dragged part would settle on.
    const candidate = this.snapCandidate();
    for (const slot of build.blueprint.slots) {
      if (build.seated.has(slot.id)) continue;
      const seat = slotOnBench(build.blueprint, build.productPlacement, slot);
      const isCandidate = candidate === slot.id;
      g.poly(outlineOf(seat, slotFaceOutline(slot), fit)).stroke({
        width: isCandidate ? 3 : 2,
        color: isCandidate ? GHOST_CANDIDATE : GHOST,
        alpha: isCandidate ? 0.95 : 0.4,
      });
    }

    // Fastener chrome over the seated parts: driven heads are real
    // hardware now, armed crossings ring up while the driving tool is in
    // hand, and the strike flashes over the head it just set.
    const toStage = (fastener: BlueprintFastener) => {
      const at = fastenerOnBench(
        build.blueprint,
        build.productPlacement,
        fastener,
      );
      return {
        x: fit.originX + at.xIn * fit.pxPerIn,
        y: fit.originY + at.yIn * fit.pxPerIn,
      };
    };
    // The way a level fastener goes in, on screen (y down, like the bench)
    const heading = (fastener: BlueprintFastener) =>
      fastenerHeadingOnBench(build.blueprint, build.productPlacement, fastener);
    for (const fastener of build.driven) {
      const { x, y } = toStage(fastener);
      const r = Math.max(0.28 * fit.pxPerIn, 2);
      const d = heading(fastener);
      if (d) {
        drawFastenerEdgeMark(g, x, y, r, Math.atan2(-d.dx, d.dy));
      } else {
        drawFastenerHead(g, x, y, r, build.blueprint.fastenerConsumable);
      }
    }
    if (this.driverHeld(build.blueprint)) {
      // With none of the drawing's fastener on hand there is nothing to
      // drive: the crossings fade to faint marks and never light up
      const short = this.onHand(build.blueprint) === 0;
      // Nails into one board end from the side sit on the same spot in
      // plan, one above the other: they ring up as one target with a
      // ring per nail still to drive, and each press takes the next.
      const stacks = new Map<string, BlueprintFastener[]>();
      for (const fastener of this.armed(build)) {
        const key = `${fastener.xIn},${fastener.yIn},${fastener.level?.headingDeg ?? "down"}`;
        stacks.set(key, [...(stacks.get(key) ?? []), fastener]);
      }
      for (const stack of stacks.values()) {
        const { x, y } = toStage(stack[0]);
        const lit = !short && stack.includes(this.hovered as BlueprintFastener);
        const color = lit ? GHOST_CANDIDATE : GHOST;
        const dim = short ? 0.35 : 1;
        const d = heading(stack[0]);
        if (d) {
          // A tail behind the head, outside the wood, shows which way
          // the nail goes in
          g.moveTo(x - d.dx * 18, y - d.dy * 18)
            .lineTo(x - d.dx * 8, y - d.dy * 8)
            .stroke({ width: lit ? 3 : 2.5, color, alpha: 0.95 * dim });
        }
        if (lit) {
          g.circle(x, y, 9).fill({ color, alpha: 0.2 });
          g.circle(x, y, 9).stroke({ width: 3, color, alpha: 1 });
        } else {
          g.circle(x, y, 7).stroke({ width: 2.5, color, alpha: 0.95 * dim });
        }
        for (let extra = 1; extra < stack.length; extra++) {
          g.circle(x, y, (lit ? 9 : 7) + extra * 3).stroke({
            width: 1.5,
            color,
            alpha: 0.6 * dim,
          });
        }
      }
    }

    // Nails with blows in them stand proud of the wood, shortening with
    // each blow until the last one sets them
    const nailLength = nailLengthPx(fit);
    const toSet = this.tuning(build.blueprint).blowsToSet;
    const drawNail = (
      x: number,
      y: number,
      lever: { dx: number; dy: number },
      from: number,
      to: number,
    ) => {
      if (to <= from) return;
      g.moveTo(x + lever.dx * from, y + lever.dy * from)
        .lineTo(x + lever.dx * to, y + lever.dy * to)
        .stroke({ width: 2.5, color: NAIL_STEEL, alpha: 1 });
      g.circle(x + lever.dx * to, y + lever.dy * to, 3.5).fill({
        color: NAIL_HEAD,
        alpha: 1,
      });
    };
    for (const [fastener, blows] of this.blows) {
      const { x, y } = toStage(fastener);
      const out = (1 - blows / toSet) * nailLength;
      // Its shadow on the wood, then the nail itself
      const lever = this.lever(build, fastener);
      g.moveTo(x + 2, y + 2)
        .lineTo(x + 2 + lever.dx * out * 0.6, y + 2 + lever.dy * out * 0.6)
        .stroke({ width: 3, color: 0x000000, alpha: 0.25 });
      drawNail(x, y, lever, 0, out);
    }

    // The pull in progress: the claw's lever over the head, the nail
    // rising out along it with the hand, point last
    const press = this.hammer.press;
    const pulling = this.pulling();
    if (press?.claw && this.pressed && pulling) {
      const { x, y } = toStage(pulling);
      const lever = this.lever(build, pulling);
      g.circle(x, y, 10).stroke({
        width: 2.5,
        color: GHOST_CANDIDATE,
        alpha: 0.95,
      });
      g.moveTo(x, y)
        .lineTo(x + lever.dx * LEVER_DRAW_PX, y + lever.dy * LEVER_DRAW_PX)
        .stroke({ width: 3.5, color: CLAW, alpha: 0.9 });
      const proud =
        (1 - (this.blows.get(pulling) ?? toSet) / toSet) * nailLength;
      const pull = nailPullFrame(
        press.claw.progress,
        nailLength,
        NAIL_CLEARANCE_PX,
      );
      drawNail(x, y, lever, Math.max(0, pull.from), Math.max(proud, pull.to));
    }

    // The last press's mark: a blow's flash on the head, a glance's
    // faint ring, a miss's puff of dust beside it, a pop's or slide's
    // fading ring
    const effect = this.effect;
    if (effect) {
      const fade = Math.max(0, effect.secondsLeft / EFFECT_SECONDS);
      switch (effect.kind) {
        case "blow":
          g.circle(effect.x, effect.y, 10).stroke({
            width: 2.5,
            color: GHOST_CANDIDATE,
            alpha: 0.95 * fade,
          });
          break;
        case "glance":
          g.circle(effect.x, effect.y, 12 - 4 * fade).stroke({
            width: 1.5,
            color: GHOST,
            alpha: 0.6 * fade,
          });
          break;
        case "miss": {
          const spread = 6 + (1 - fade) * 10;
          for (const [ox, oy] of [
            [-1, -0.6],
            [0.7, -1],
            [1, 0.4],
          ]) {
            g.circle(effect.x + ox * spread, effect.y + oy * spread, 2).fill({
              color: DUST,
              alpha: 0.8 * fade,
            });
          }
          break;
        }
        case "pop":
        case "slide":
          g.circle(effect.x, effect.y, 8 + (1 - fade) * 6).stroke({
            width: 2,
            color: GHOST_CANDIDATE,
            alpha: 0.8 * fade,
          });
          break;
      }
    }
  }

  /** Which outline the dragged piece is hovering over, if any. */
  private snapSlotFor(
    build: NonNullable<ReturnType<BenchAssemblyView["build"]>>,
    materialId: string,
  ): string | null {
    const piece = build.pieces.find(
      (candidate) => candidate.material.id === materialId,
    );
    if (!piece) return null;
    // Follow the hand, not the sim: nothing writes the piece's placement
    // until it is set down, so the committed one is where it was picked up.
    const placement =
      this.game.entities.tryGetSingleton(BenchArrangeView)?.dragPlacement() ??
      piece.placement;
    const taken = new Set(
      [...build.seated.entries()]
        .filter(([, id]) => id !== materialId)
        .map(([slotId]) => slotId),
    );
    return (
      snapPlacementFor(
        build.blueprint,
        build.productPlacement,
        piece.material,
        placement,
        taken,
      )?.slotId ?? null
    );
  }
}

/** A slot's outline on the stage, in screen px: its face outline (the
 * miters cut, as the piece laid on it will be) turned to its seat. */
function outlineOf(
  seat: { xIn: number; yIn: number; angleDeg: number },
  outline: ReadonlyArray<{ x: number; y: number }>,
  fit: StageFit,
): number[] {
  const radians = (seat.angleDeg * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return outline.flatMap(({ x: dx, y: dy }) => [
    fit.originX + (seat.xIn + dx * cos - dy * sin) * fit.pxPerIn,
    fit.originY + (seat.yIn + dx * sin + dy * cos) * fit.pxPerIn,
  ]);
}

/** The drawn nail's length at this zoom — a real nail's, up to a cap
 * that keeps it a nail and not a spike at the closest lean-in. */
function nailLengthPx(fit: StageFit): number {
  return Math.min(NAIL_LENGTH_IN * fit.pxPerIn, NAIL_LENGTH_MAX_PX);
}
